import { computedFrom } from "aurelia-binding";
import { autoinject, transient } from "aurelia-dependency-injection";
import { bindable } from "aurelia-templating";
import * as Enumerable from "linq";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { Reflection } from "../../../../../../../utilities/reflection";
import { PortCallStatusEnum, PortCallSummaryItem } from "../../../models/ports-and-terminals.models";
import { PortsAndTerminalsService } from "../../../services/ports-and-terminals.service";

@autoinject()
@transient()
export class VesselPortCallsSummary {

    public statuses: { id: number, name: string }[] = [];
    public riskLevels: { id: number, name: string }[] = [];

    @bindable()
    public portCallSummaryItems: PortCallSummaryItem[] = [];

    @computedFrom(
        Reflection.getPropertyPath<VesselPortCallsSummary>(x => x.portCallSummaryItems),
        Reflection.getPropertyPath<VesselPortCallsSummary>(x => x.riskLevels)
    )
    public get portCallSummaryItemsByRisk(): { risk: string, vesselCount: number, items: PortCallSummaryItem[] }[] {
        const results: { risk: string, vesselCount: number, items: PortCallSummaryItem[] }[] = [];

        if (this.statuses.length == 0) {
            return [];
        }

        Enumerable.from(this.portCallSummaryItems)
            .groupBy(x => x.risk)
            .forEach(group => {
                results.push({
                    risk: group.key(),
                    vesselCount: group.sum(y => y.vesselCount),
                    items: this.consolidatePortCallSummaryItems(group.key(), group.toArray())
                });
            });
        const allRisks = this.riskLevels.map(x => x.name);
        allRisks.forEach(risk => {
            if (!results.some(x => x.risk.toLowerCase() == risk.toLowerCase())) {
                results.push({
                    items: [],
                    risk: risk,
                    vesselCount: 0
                });
            }
        });
        return results.filter(x => x.risk?.toLowerCase() != 'pending');
    }

    @computedFrom('portCallSummaryItems')
    public get pendingVesselCount(): number {
        const vesselCount = this.portCallSummaryItems.filter(x => x.risk?.toLowerCase() == "pending")
            .map(x => x.vesselCount)
            .reduce((sum, current) => sum + current, 0);

        return vesselCount ?? 0;
    }

    @computedFrom('portCallSummaryItems')
    public get totalVesselCount(): number {
        return Enumerable.from(this.portCallSummaryItems).sum(x => x.vesselCount);
    }

    constructor(private portsAndTerminalsService: PortsAndTerminalsService) {
    }

    attached() {
        const p1 = this.portsAndTerminalsService.loadPortCallStatus();
        const p2 = this.portsAndTerminalsService.loadScreeningRisks();
        Promise.all([p1, p2]).then(results => {
            this.statuses = results?.length > 0 ? results[0].map(x => { return { id: x.statusId, name: x.statusName }; }) : [];
            this.riskLevels = results?.length > 1 ? results[1].map(x => { return { id: x.screeningRiskId, name: x.screeningRiskName }; }) : [];
        });
    }

    //Merge reviewed and reviewed with warning in 1 item with status reviewed
    private consolidatePortCallSummaryItems(risk: string, items: PortCallSummaryItem[]) {
        const consolidatesItems: PortCallSummaryItem[] = [];

        const reviewStatus = this.statuses.filter(x => x.id == PortCallStatusEnum.Reviewed)[0];
        const reviewWithWarningStatus = this.statuses.filter(x => x.id == PortCallStatusEnum.ReviewedWithWarnings)[0];

        Enumerable.from(items)
            .orderBy(x => x.reviewStatus == reviewWithWarningStatus.name ? 1 : 0)
            .forEach(item => {
                if (item.reviewStatus != reviewWithWarningStatus.name) {
                    consolidatesItems.push(CloneHelper.clone(item));
                } else {
                    let existingConsolidatesItem = consolidatesItems.filter(x => x.reviewStatus == reviewStatus.name)[0];
                    if (existingConsolidatesItem == null) {
                        existingConsolidatesItem = {
                            reviewStatus: reviewStatus.name,
                            risk: risk,
                            vesselCount: 0
                        };
                        consolidatesItems.push(existingConsolidatesItem);
                    }
                    existingConsolidatesItem.vesselCount += item.vesselCount;
                }
            });
        return consolidatesItems;
    }
}